import { keyframes } from "@emotion/react";
import { Label } from "@headlessui/react";
import { TFunction } from "i18next";
import { HTTPError } from "ky";
import { Check, CheckCircleIcon, XIcon } from "lucide-react";
import {
  ComponentPropsWithoutRef,
  Fragment,
  useEffect,
  useRef,
  useState,
} from "react";
import { SubmitHandler, useForm } from "react-hook-form";
import { useTranslation } from "react-i18next";
import { QrReader } from "react-qr-reader";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import tw from "twin.macro";

import { useCreatePost } from "@/api/areas/mutations";
import { useAreas } from "@/api/areas/queries";
import { Area, Post } from "@/api/areas/types";
import { useClients } from "@/api/clients/queries";
import { Client } from "@/api/clients/types";
import { Button } from "@/components/buttons";
import Field from "@/components/field";
import Input from "@/components/inputs";
import { PageContainer, PageSubTitle, PageTitle } from "@/components/page";
import Select from "@/components/selector";
import { usePersistedState } from "@/hooks/usePersistedState";

// ---------------------------------------------------------------------------
// Toasts
// ---------------------------------------------------------------------------

const showFailureToast = (
  translationKey: string,
  translator: TFunction<"translation", undefined>
) => {
  toast.custom(
    (toastId) => (
      <div tw="relative w-full h-full">
        <div
          role="status"
          tw="flex items-start justify-center gap-3 rounded-lg bg-red-500 px-4 py-3 shadow-sm min-w-[280px] max-w-[360px] absolute top-0 right-0 min-[604px]:static"
        >
          <div tw="flex shrink-0 bg-white items-center justify-center rounded-full w-8 h-8 mt-3">
            <XIcon tw="w-5 h-5 text-red-500" />
          </div>
          <div tw="flex flex-1 flex-col gap-1 min-w-0">
            <p tw="text-lg font-bold text-white">
              {translator("quickPost.error")}
            </p>
            <p tw="text-base font-medium text-white">
              {/* eslint-disable-next-line @typescript-eslint/no-explicit-any */}
              {translator(translationKey as any)}
            </p>
          </div>
          <button
            type="button"
            aria-label={translator("global.close")}
            onClick={() => toast.dismiss(toastId)}
            tw="shrink-0 rounded p-1 text-white hover:bg-red-600 focus:(outline-none ring-2 ring-offset-1 ring-red-600)"
          >
            <XIcon tw="w-4 h-4" />
          </button>
        </div>
      </div>
    ),
    { duration: Infinity, id: "quick-post-module-failure" }
  );
};

// ---------------------------------------------------------------------------
// Styles
// ---------------------------------------------------------------------------

const greenBorderFade = keyframes({
  from: { borderColor: "rgb(34, 197, 94)" },
  to: { borderColor: "transparent" },
});

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

interface ScanDataValues {
  name: string;
  address: number;
}

// ---------------------------------------------------------------------------
// QuickPostAdd
// ---------------------------------------------------------------------------

const QuickPostAdd = () => {
  const { t } = useTranslation();
  const { data: clients } = useClients();
  const { data: areas } = useAreas();

  const [scanData, setScanData] = useState<ScanDataValues>({
    name: "",
    address: 0,
  });

  const [selectedClient, setSelectedClient] = usePersistedState<Client | null>(
    "quickpost:selectedClient",
    null
  );
  const [selectedArea, setSelectedArea] = usePersistedState<Area | null>(
    "quickpost:selectedArea",
    null
  );
  const [areaArray, setAreaArray] = usePersistedState<Area[]>(
    "quickpost:areaArray",
    []
  );

  const [showGreenBorder, setShowGreenBorder] = useState(false);
  const [showCheckCircle, setShowCheckCircle] = useState(false);
  const dataFormRef = useRef<HTMLDivElement>(null);

  // Invalide le client persisté s'il n'existe plus côté serveur
  useEffect(() => {
    if (!clients || !selectedClient) return;
    const stillExists = clients.some((c) => c._id === selectedClient._id);
    if (!stillExists) setSelectedClient(null);
  }, [clients]);

  // Invalide l'area persistée si elle n'existe plus côté serveur
  useEffect(() => {
    if (!areas || !selectedArea) return;
    const stillExists = areas.some((a) => a._id === selectedArea._id);
    if (!stillExists) setSelectedArea(null);
  }, [areas]);

  const handleSelectClient = (e: React.ChangeEvent<HTMLSelectElement>) => {
    const client = clients?.find((client) => client._id === e.target.value);
    setSelectedClient(client ?? null);

    if (client) {
      setAreaArray(
        // @ts-expect-error - areas is not defined
        areas ? areas.filter((area) => client.areas?.includes(area._id)) : []
      );
    } else {
      setAreaArray([]);
      setSelectedArea(null);
    }
  };

  const handleSelectArea = (e: React.ChangeEvent<HTMLSelectElement>) => {
    setSelectedArea(
      areaArray.find((area) => area._id === e.target.value) ?? null
    );
  };

  useEffect(() => {
    if (!showGreenBorder && !showCheckCircle) return;

    let greenBorderTimeout: ReturnType<typeof setTimeout> | undefined;
    let checkCircleTimeout: ReturnType<typeof setTimeout> | undefined;

    if (showGreenBorder) {
      greenBorderTimeout = setTimeout(
        () => setShowGreenBorder(false),
        8 * 1000
      );
    }

    if (showCheckCircle) {
      checkCircleTimeout = setTimeout(
        () => setShowCheckCircle(false),
        3 * 1000
      );
    }

    dataFormRef.current?.scrollIntoView({ behavior: "smooth", block: "start" });

    return () => {
      clearTimeout(greenBorderTimeout);
      clearTimeout(checkCircleTimeout);
    };
  }, [showGreenBorder, showCheckCircle]);

  return (
    <PageContainer>
      <div tw="w-full max-w-7xl px-4 sm:px-6 lg:px-8">
        <div tw="max-w-7xl flex flex-col gap-8">
          <div tw="flex flex-col gap-4">
            <PageTitle>{t("quickPost.title")}</PageTitle>
            <PageSubTitle>{t("quickPost.subtitle")}</PageSubTitle>
          </div>

          <Field tw="flex-1 sm:w-64">
            <Select
              onChange={handleSelectClient}
              value={selectedClient?._id ?? ""}
            >
              <option value="">{t("form.selectCustomer")}</option>
              {clients && clients.length > 0 && (
                <Fragment>
                  {clients.map((client: Client) => (
                    <option key={client._id} value={client._id}>
                      {client.name}
                    </option>
                  ))}
                </Fragment>
              )}
            </Select>
          </Field>

          <Field
            tw="flex-1 sm:w-64 transition-opacity duration-1000 ease-out opacity-0"
            css={[selectedClient && tw`opacity-100`]}
          >
            <Select onChange={handleSelectArea} value={selectedArea?._id ?? ""}>
              <option value="">{t("form.selectArea")}</option>
              <Fragment>
                {areaArray.map((area: Area) => (
                  <option key={area._id} value={area._id}>
                    {area.name}
                  </option>
                ))}
              </Fragment>
            </Select>
          </Field>

          <div
            tw="max-w-xl flex flex-col gap-8 transition-opacity duration-1000 ease-out opacity-0"
            css={[selectedArea && tw`opacity-100 delay-200`]}
          >
            <div tw="isolate relative rounded-3xl overflow-hidden bg-black">
              {showCheckCircle && (
                <div tw="absolute inset-0 flex items-center justify-center z-10">
                  <CheckCircleIcon tw="w-10 h-10 text-green-500" />
                </div>
              )}
              <QrReader
                constraints={{
                  aspectRatio: { ideal: 1 },
                  facingMode: "environment",
                }}
                videoId="qrScanner"
                ViewFinder={(props) => (
                  <Mask {...props} showGreenBorder={showGreenBorder} />
                )}
                scanDelay={500}
                onResult={async (result) => {
                  if (!result) return;
                  const text = result?.getText();
                  if (text !== undefined && text !== null) {
                    const lastDigitsMatch = text.match(/(\d+)(?!.*\d)/);
                    if (!lastDigitsMatch) return;

                    setScanData({
                      name: scanData.name,
                      address: Number(lastDigitsMatch[1]),
                    });
                    setShowGreenBorder(true);
                    setShowCheckCircle(true);
                  }
                }}
              />
            </div>

            {selectedArea && selectedClient && (
              <div ref={dataFormRef}>
                <DataForm
                  clientId={selectedClient._id}
                  area={selectedArea}
                  post={scanData}
                  setPost={setScanData}
                />
              </div>
            )}
          </div>
        </div>
      </div>
    </PageContainer>
  );
};

export default QuickPostAdd;

// ---------------------------------------------------------------------------
// Mask
// ---------------------------------------------------------------------------

type MaskProps = ComponentPropsWithoutRef<"svg"> & {
  color?: string;
  showGreenBorder?: boolean;
};

const Mask = ({
  color = "currentColor",
  showGreenBorder,
  ...props
}: MaskProps) => (
  <svg
    viewBox="0 0 100 100"
    tw="absolute inset-0 z-10 border-black/40 border-[2rem] text-primary-200"
    css={[
      showGreenBorder && {
        borderColor: "rgb(34, 197, 94)",
        animationName: greenBorderFade,
        animationDuration: "8s",
        animationTimingFunction: "ease-out",
        animationFillMode: "forwards",
      },
    ]}
    {...props}
  >
    <path fill="none" d="M13,0 L0,0 L0,13" stroke={color} strokeWidth="5" />
    <path fill="none" d="M0,87 L0,100 L13,100" stroke={color} strokeWidth="5" />
    <path
      fill="none"
      d="M87,100 L100,100 L100,87"
      stroke={color}
      strokeWidth="5"
    />
    <path fill="none" d="M100,13 L100,0 87,0" stroke={color} strokeWidth="5" />
  </svg>
);

// ---------------------------------------------------------------------------
// DataForm
// ---------------------------------------------------------------------------

interface DataFormProps {
  clientId: string;
  area: Area;
  post: ScanDataValues;
  setPost: (data: ScanDataValues) => void;
}

const DataForm = ({ clientId, area, post }: DataFormProps) => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const createPost = useCreatePost({ clientId, areaId: area._id });

  const [location, setLocation] = useState({ latitude: "", longitude: "" });

  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const success = (position: any) => {
    setLocation({
      latitude: position.coords.latitude,
      longitude: position.coords.longitude,
    });
  };

  const handleGetLocation = () => {
    if (navigator.geolocation) {
      navigator.geolocation.getCurrentPosition(success, () => {
        toast.error("Unable to retrieve your location");
      });
    } else {
      toast.error("Geolocation not supported");
    }
  };

  const { register, handleSubmit, reset } = useForm<Post>({
    defaultValues: {
      name: post.name || "",
      address: post.address || undefined,
      latitude: location.latitude || "",
      longitude: location.longitude || "",
    },
  });

  const showSuccessToast = (moduleAddress: number) => {
    const fichePath = `/clients/${clientId}/areas/${area._id}/post/${moduleAddress}`;
    toast.custom(
      (toastId) => (
        <div tw="relative w-full h-full">
          <div
            role="status"
            tw="flex items-start gap-3 rounded-lg border border-green-200/80 px-4 py-3 shadow-sm min-w-[280px] max-w-[360px] absolute top-0 right-0 min-[604px]:static"
            style={{ backgroundColor: "#dcfce7" }}
          >
            <div
              tw="flex shrink-0 items-center justify-center rounded-full w-8 h-8"
              style={{ backgroundColor: "#15803d" }}
            >
              <Check tw="w-5 h-5 text-white" />
            </div>
            <div tw="flex flex-1 flex-col gap-1 min-w-0">
              <p tw="text-sm font-medium text-black">
                {t("quickPost.toastSuccess").split("{{address}}")[0]}
                <span style={{ color: "#2563eb", fontWeight: 600 }}>
                  {moduleAddress}
                </span>
                {t("quickPost.toastSuccess").split("{{address}}")[1]}
              </p>
              <button
                type="button"
                onClick={() => {
                  toast.dismiss(toastId);
                  navigate(fichePath);
                }}
                tw="text-sm font-medium text-left hover:underline focus:(outline-none ring-2 ring-offset-1 ring-green-500 rounded)"
                style={{ color: "#2563eb" }}
              >
                {t("quickPost.toastViewSheet")}
              </button>
            </div>
            <button
              type="button"
              aria-label={t("global.close")}
              onClick={() => toast.dismiss(toastId)}
              tw="shrink-0 rounded p-1 text-green-700 hover:bg-green-200/60 focus:(outline-none ring-2 ring-offset-1 ring-green-600)"
            >
              <XIcon tw="w-4 h-4" />
            </button>
          </div>
        </div>
      ),
      { duration: Infinity, id: "quick-post-module-created" }
    );
  };

  const onSubmit: SubmitHandler<Post> = async (data) => {
    try {
      await createPost.mutateAsync({
        address: +data.address,
        latitude: data.latitude.toString(),
        longitude: data.longitude.toString(),
      });

      showSuccessToast(Number(data.address));
      setLocation({ latitude: "", longitude: "" });
      reset();
    } catch (error) {
      if (error instanceof HTTPError) {
        switch (error.response.status) {
          case 409:
            showFailureToast("quickPost.moduleAlreadyExists", t);
            break;
          default:
            showFailureToast("quickPost.toastFailure", t);
        }
        return;
      }
      showFailureToast("quickPost.toastFailure", t);
    }
  };

  useEffect(() => {
    reset({ name: post.name, address: post.address });
  }, [post]);

  useEffect(() => {
    reset({ latitude: location.latitude, longitude: location.longitude });
  }, [location]);

  return (
    <form onSubmit={handleSubmit(onSubmit)} autoComplete="off">
      <div tw="grid grid-cols-2 gap-4">
        <Field>
          <Label>{t("form.addPosts.address")}</Label>
          <Input {...register(`address`)} />
        </Field>
      </div>
      <div tw="grid grid-cols-2 gap-4">
        <Field>
          <Label>{t("form.addArea.latitude")}</Label>
          <Input {...register(`latitude`)} />
        </Field>
        <Field>
          <Label>{t("form.addArea.longitude")}</Label>
          <Input {...register(`longitude`)} />
        </Field>
      </div>
      <div
        tw="transition w-full mt-8 flex items-center justify-center gap-1.5 rounded-b-xl rounded-tl-xl px-4 py-2 text-center text-sm font-semibold border border-transparent focus:(ring-2 ring-offset-2 outline-none) disabled:(opacity-70 cursor-not-allowed) bg-white border-gray-300 text-gray-700 hover:bg-gray-100 focus:(ring-gray-500)"
        onClick={handleGetLocation}
      >
        {t("action.getLocation")}
      </div>
      <div tw="flex w-full justify-end">
        <Button tw="mt-8" primary type="submit">
          {t("action.save")}
        </Button>
      </div>
    </form>
  );
};
